import React, { useContext, useEffect, useState } from "react";
import {
  Button,
  Stack,
  Text,
  Box,
  Loader,
  Center,
  Paper,
  useMantineTheme,
  Group,
  Image,
} from "@mantine/core";
import {
  IconShieldCheck,
  IconLock,
  IconId,
} from "@tabler/icons-react";
import { DataContext } from "../../../context/DataContext";
import useStatusCheck from "../../../hooks/useStatusCheck";
import useEdgeStatusCheck from "../../../hooks/useEdgeStatusCheck";
import useLocale from "../../../hooks/useLocale";
import { getEdgeQueueUrl, getEdgeRetrieveUrl } from "../../../utils/edge";
import CpfLogo from "../../../assets/cpf.png";

const BrazilCpfStart = () => {
  const { settings, data, dispatch } = useContext(DataContext);
  const theme = useMantineTheme();
  const locale = useLocale("brazil-cpf");
  const [url, setUrl] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [windowOpened, setWindowOpened] = useState(false);

  // Features with localized text
  const features = [
    { icon: IconId, text: locale.features.cpfNumber },
    { icon: IconShieldCheck, text: locale.features.secureVerification },
    { icon: IconLock, text: locale.features.dataProtected },
  ];

  // Use edge status check when edge is true and user has opened the verification window
  const isEdgeMode = data?.edge === true;
  useStatusCheck(!isEdgeMode, false, "idmax");
  useEdgeStatusCheck(isEdgeMode && windowOpened);

  // Open verification in popup window
  const handleVerifyClick = () => {
    if (url) {
      window.open(url, "_blank");
      setWindowOpened(true);
    }
  };

  // Polling function for edge mode
  const pollForUrl = async (requestId) => {
    const maxAttempts = 100;
    const pollInterval = 3000;
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await fetch(getEdgeRetrieveUrl(), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ request_id: requestId }),
        });

        if (response.ok) {
          const result = await response.json();
          console.log("Poll response:", result);

          if (result.request_url) {
            setUrl(result.request_url);
            setLoading(false);
            return;
          }
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          setError(locale.errors.timeout);
          setLoading(false);
        }
      } catch (error) {
        console.error("Polling error:", error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          setError(locale.errors.fetchUrl);
          setLoading(false);
        }
      }
    };

    poll();
  };

  useEffect(() => {
    if (url) return;

    const fetchUrl = async () => {
      data.provider = "TRINSIC";
      sessionStorage.setItem("trinsic_provider", "TRINSIC");
      // Store request_id for edge mode return flow
      if (settings?.request_id) {
        sessionStorage.setItem("edge_request_id", settings.request_id);
      }

      try {
        let apiUrl;
        let requestData = { ...data };

        if (isEdgeMode) {
          apiUrl = getEdgeQueueUrl();
          requestData.queue = "idmax";
        } else {
          apiUrl = process.env.REACT_APP_BACKEND_URL + "/v3/stable/api/frontend/idmax";
        }

        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(requestData),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data_returned = await response.json();
        console.log("Brazil CPF response:", data_returned);

        if (isEdgeMode) {
          const reqId = data_returned.request_id || data.request_id;
          if (reqId) {
            setRequestId(reqId);
            pollForUrl(reqId);
          } else {
            setError(locale.errors.noRequestId);
            setLoading(false);
          }
        } else {
          // Normal mode - get URL directly from response
          const verifyUrl = data_returned.continuations?.info?.template?.info?.request_url;
          if (verifyUrl) {
            setUrl(verifyUrl);
            setLoading(false);
          } else {
            setError(locale.errors.noVerificationUrl);
            setLoading(false);
          }
        }
      } catch (error) {
        console.error("Error fetching URL:", error);
        setError(locale.errors.startingVerification);
        setLoading(false);
      }
    };

    fetchUrl();
  }, [url, isEdgeMode]);

  if (error) {
    return (
      <Center style={{ height: "100%" }}>
        <Stack align="center">
          <Text color="red">{error}</Text>
          <Button onClick={() => window.location.reload()}>{locale.button.tryAgain}</Button>
        </Stack>
      </Center>
    );
  }

  return (
    <Box
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        height: "100%",
      }}
    >
      <Box>
        {/* Header */}
        <Stack align="center" spacing={0} mb="md">
          <Group spacing={6}>
            <IconId size={28} />
            <Text fz="xl" fw={700}>
              {locale.header.title}
            </Text>
          </Group>
          <Text fz="sm" c="dimmed">
            {locale.header.subtitle}
          </Text>
        </Stack>

        {/* Features */}
        <Center mb="md">
          <Paper
            p="lg"
            withBorder
            style={{
              backgroundColor: theme.colorScheme === "dark" ? theme.colors.dark[6] : "#fafafa",
            }}
          >
            <Stack>
              {features.map((feature, index) => {
                const FeatureIcon = feature.icon;
                return (
                  <Group noWrap key={index}>
                    <FeatureIcon size={20} />
                    <Text fz="md">{feature.text}</Text>
                  </Group>
                );
              })}
            </Stack>
          </Paper>
        </Center>

        {/* Loading or waiting state */}
        {loading && (
          <Center mt="xl">
            <Stack align="center">
              <Loader size="lg" color={theme.primaryColor} />
              <Text c="dimmed">{locale.loading.preparing}</Text>
            </Stack>
          </Center>
        )}

        {/* After window opened - waiting for completion */}
        {windowOpened && !loading && (
          <Center mt="xl">
            <Stack align="center">
              <Loader size="lg" color={theme.primaryColor} />
              <Text c="dimmed">{locale.loading.waiting}</Text>
              <Text size="xs" c="dimmed">
                {locale.loading.completeInPopup}
              </Text>
            </Stack>
          </Center>
        )}
      </Box>

      {/* Button and logo at bottom */}
      <Box>
        {!loading && url && !windowOpened && (
          <Button
            size="lg"
            fullWidth
            onClick={handleVerifyClick}
          >
            {locale.button.startVerification}
          </Button>
        )}

        {/* Footer with logo */}
        <Center mt="md">
          <Image src={CpfLogo} width={100} alt="CPF" />
        </Center>
      </Box>
    </Box>
  );
};

export default BrazilCpfStart;
